/* Renaissance Block — theme behaviour.
 *
 * Ported from the prototype's site.js, minus everything that faked commerce.
 * The prototype kept its own bag array in localStorage and stored a fabricated
 * price on each line; here the cart is Shopify's, and the drawer is re-rendered
 * from the server through the Section Rendering API so displayed totals can
 * never drift from what checkout will charge.
 *
 * The watchlist stays in localStorage under the prototype's original key, so a
 * repointed domain keeps existing visitors' lists.
 */

/* Each concern below is its own scope AND its own failure.
 *
 * They were bare top-level IIFEs, which reads as four independent pieces and is
 * not: a throw inside any one of them aborts evaluation of the whole file, so
 * everything declared after it never runs. That is how the header overlay — the
 * last but one — could go missing on the home page with nothing in the markup or
 * the stylesheet to explain it, and it would have taken live search with it.
 *
 * Wrapping each one buys back the isolation the comments already claim. A broken
 * concern now costs exactly itself, and says so in the console rather than
 * failing silently three concerns later.
 */
const part = (name, fn) => {
  try {
    fn();
  } catch (err) {
    console.error(`[renaissance-block] ${name} failed and was skipped:`, err);
  }
};

part('theme behaviour', () => {
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  const WATCH_KEY = 'block_v7_watch';
  const LEGACY_KEYS = ['block_v6_watch', 'block_v5_watch'];

  /* ---------------------------------------------------------------- toast -- */
  let toastTimer;
  const toast = (msg) => {
    const t = $('#toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('show'), 1600);
  };

  /* ------------------------------------------------------------ watchlist -- */
  const readWatch = () => {
    for (const key of [WATCH_KEY, ...LEGACY_KEYS]) {
      try {
        const raw = localStorage.getItem(key);
        if (raw) return JSON.parse(raw);
      } catch { /* corrupt or blocked storage — fall through */ }
    }
    return [];
  };

  let watch = readWatch();

  /* Price marks: what each watched product cost at the moment it was watched.
   *
   * A SECOND key, deliberately. block_v7_watch is an array of ids, it is read
   * with includes() and filter() in several places here, and it carries lists
   * built by the prototype under a domain that has not moved yet. Migrating its
   * shape to hold prices would put every one of those lists at risk to add a
   * decoration; a separate key cannot break anything that exists.
   *
   * { "<product id>": { p: <price in cents>, t: <epoch ms> } }
   */
  const MARK_KEY = 'block_v7_watch_marks';

  const readMarks = () => {
    try {
      const raw = localStorage.getItem(MARK_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      /* An array here would be a corrupt or foreign value; typeof null is
         'object', so both have to be excluded by hand. */
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch { /* corrupt or blocked storage */ }
    return {};
  };

  let marks = readMarks();

  const saveMarks = () => {
    try { localStorage.setItem(MARK_KEY, JSON.stringify(marks)); } catch { /* private mode */ }
  };

  /* The move since watching, as the card prints it. Returns null when there is
     nothing honest to say — no mark, or a recorded price of zero, which would
     make the percentage a division by nothing rather than a fall of 100%. */
  const sinceWatched = (id, nowCents) => {
    const mark = marks[String(id)];
    if (!mark || !Number.isFinite(mark.p) || mark.p <= 0) return null;
    if (!Number.isFinite(nowCents)) return null;
    if (nowCents === mark.p) return 'HELD / SINCE WATCHED';
    const pct = ((nowCents - mark.p) / mark.p) * 100;
    /* The same minus glyph and the same one decimal as .card-delta, so the two
       figures are set identically wherever they appear together. */
    const sign = pct < 0 ? '\u2212' : '+';
    return `${sign}${Math.abs(pct).toFixed(1)}% / SINCE WATCHED`;
  };

  const saveWatch = () => {
    try { localStorage.setItem(WATCH_KEY, JSON.stringify(watch)); } catch { /* private mode */ }
    paintWatch();
  };

  const isWatched = (id) => watch.includes(String(id));

  function paintWatch() {
    const n = String(watch.length).padStart(2, '0');
    const count = $('#watchCount');
    if (count) count.textContent = n;
    const total = $('#watchTotal');
    if (total) total.textContent = n;

    $$('[data-watch]').forEach((b) => {
      const on = isWatched(b.dataset.watch);
      /* Only relabel the word buttons. The card's control is an icon, and
         setting textContent on it would replace the mark with the word.
         aria-pressed already carries the state for both. */
      if (!b.firstElementChild) b.textContent = on ? 'WATCHING' : 'WATCH';
      b.classList.toggle('watching', on);
      b.setAttribute('aria-pressed', String(on));
    });

    // Watchlist page: reveal only the watched rows.
    const page = $('[data-watchlist]');
    if (page) {
      let shown = 0;
      let dirty = false;
      $$('[data-watch-item]', page).forEach((el) => {
        const id = String(el.dataset.watchItem);
        const on = isWatched(id);
        el.hidden = !on;
        if (!on) return;
        shown++;

        /* Watched before this shipped, so there is no mark to compare against.
           Start the clock now rather than printing a 0.0% that was never
           measured — the figure is only worth anything if it is real. */
        const now = Number(el.dataset.price);
        if (!marks[id] && Number.isFinite(now) && now > 0) {
          marks[id] = { p: now, t: Date.now() };
          dirty = true;
        }

        const slot = $('[data-since-watched]', el);
        if (!slot) return;
        const text = sinceWatched(id, now);
        slot.textContent = text || '';
        slot.hidden = !text;
      });
      if (dirty) saveMarks();
      const empty = $('#watchEmpty');
      if (empty) empty.hidden = shown > 0;
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-watch]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const id = String(btn.dataset.watch);
    const on = isWatched(id);
    watch = on ? watch.filter((x) => x !== id) : [...watch, id];

    /* The mark is made and unmade with the entry itself, so a product watched,
       dropped and watched again is measured from the second time — which is
       what "since watched" means. */
    if (on) {
      delete marks[id];
    } else {
      const price = Number(btn.dataset.watchPrice);
      if (Number.isFinite(price) && price > 0) marks[id] = { p: price, t: Date.now() };
    }
    saveMarks();

    saveWatch();
    toast(on ? 'REMOVED FROM WATCHLIST' : 'ADDED TO WATCHLIST');
  });

  /* ----------------------------------------------------------- bag drawer -- */
  const bag = $('#bag');
  const scrim = $('#scrim');

  const openBag = () => {
    if (!bag) return;
    bag.classList.add('open');
    bag.setAttribute('aria-hidden', 'false');
    scrim?.classList.add('open');
    $('#bagOpen')?.setAttribute('aria-expanded', 'true');
    bag.querySelector('[data-close]')?.focus();
  };

  const closeBag = () => {
    if (!bag) return;
    bag.classList.remove('open');
    bag.setAttribute('aria-hidden', 'true');
    scrim?.classList.remove('open');
    $('#bagOpen')?.setAttribute('aria-expanded', 'false');
  };

  document.addEventListener('click', (e) => {
    if (e.target.closest('#bagOpen') || e.target.closest('#mobileBarOpen')) { e.preventDefault(); openBag(); }
    else if (e.target.closest('[data-close]') || e.target === scrim) { e.preventDefault(); closeBag(); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && bag?.classList.contains('open')) closeBag();
  });

  /* Re-render the drawer from the server rather than patching it locally. */
  /* Quantity and remove inside the drawer. The links are real /cart/change
     navigations so they work without JS; intercepted here they update the
     drawer in place instead of reloading the page and closing the bag. */
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('#bag a[href*="/cart/change"]');
    if (!link) return;
    e.preventDefault();
    const url = new URL(link.href, window.location.origin);
    /* The drawer addresses lines by key; `line` is still accepted so an older
       cached render, or a link from anywhere else, keeps working. */
    const key = url.searchParams.get('id');
    const line = url.searchParams.get('line');
    const quantity = url.searchParams.get('quantity');
    if (quantity == null || (key == null && line == null)) return;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...(key == null ? { line: Number(line) } : { id: key }),
          quantity: Number(quantity),
          sections: ['cart-drawer'],
          sections_url: window.location.pathname + window.location.search,
        }),
      });
      if (!res.ok) throw new Error('Could not update the bag');
      const json = await res.json();
      if (!applyDrawer(json?.sections?.['cart-drawer'])) location.assign(link.href);
    } catch (err) {
      toast(String(err.message).toUpperCase());
    }
  });

  /* Bundled section rendering: ask /cart/add.js to return the re-rendered
     drawer in the same response. A separate ?sections= request renders against
     the PUBLISHED theme, so on a draft theme it comes back null and the drawer
     silently keeps its stale, empty markup. */
  async function addToCart(items) {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname + window.location.search,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || 'Could not add to bag');
    }
    const json = await res.json();
    /* Sections render after the mutation and can come back null on an HTTP 200.
       The change path already falls back to a reload; without the same guard
       here the item is in the cart but the drawer opens on its previous, often
       empty, markup. */
    if (!applyDrawer(json?.sections?.['cart-drawer'])) {
      location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      return json;
    }
    return json;
  }

  /* Swap the drawer's contents from freshly rendered section markup. */
  function applyDrawer(html) {
    if (!html || !bag) return false;
    const next = new DOMParser().parseFromString(html, 'text/html');
    const items = next.querySelector('#bagItems');
    const foot = next.querySelector('.drawer-foot');
    if (!items || !foot) return false;
    const live = $('#bagItems');
    live.innerHTML = items.innerHTML;
    /* innerHTML replaces the children, not the element, so data-count would keep
       whatever Liquid rendered on first paint. Carry it over or anything reading
       the attribute later gets a stale answer. */
    live.dataset.count = items.dataset.count ?? '';
    bag.querySelector('.drawer-foot').innerHTML = foot.innerHTML;
    /* The free-shipping bar is a function of the cart total, so it is as stale
       as the foot after an add and has to come across with it. Rendered by
       Liquid, which is the only thing that knows the threshold — deriving it
       here would put the number in two places. */
    const shipNext = next.querySelector('#bagShip');
    const ship = $('#bagShip');
    if (ship && shipNext) ship.innerHTML = shipNext.innerHTML;
    /* data-count is cart.item_count, rendered by Liquid. Counting .bag-line
       counts LINES, so two of the same size read as 01 while a page reload
       reads 02 — and /cart/add.js cannot settle it either, because it returns
       only the lines it added and no cart totals at all. */
    const rows = next.querySelectorAll('.bag-line').length;
    const counted = Number(items.dataset.count);
    const n = Number.isFinite(counted) ? counted : rows;
    const badge = $('#bagCount');
    if (badge) badge.textContent = String(Math.min(n, 99)).padStart(2, '0');

    const barNext = next.querySelector('#mobileBar');
    const bar = $('#mobileBar');
    if (bar && barNext) {
      bar.innerHTML = barNext.innerHTML;
      bar.hidden = rows === 0;
    }
    return true;
  }

  /* Intercept add-to-cart forms; without JS they still post normally. */
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[action*="/cart/add"]')) return;
    const id = form.querySelector('[name="id"]')?.value;
    if (!id) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    btn?.setAttribute('disabled', '');
    try {
      const qty = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
      const added = await addToCart([{ id: Number(id), quantity: qty }]);
      const label = added?.items?.[0]?.title || 'ITEM';
      toast(`${label.toUpperCase()} / ADDED`);
      openBag();
    } catch (err) {
      toast(String(err.message).toUpperCase());
    } finally {
      btn?.removeAttribute('disabled');
    }
  });


  /* Change a line's size without leaving the bag.

     Shopify has no call that swaps the variant on an existing line: change.js
     only edits quantity, properties and selling plan. So this adds the new
     variant and then removes the old line — that order, not the reverse,
     because /cart/add.js validates inventory and will 422 on a sold-out size.
     Removing first would empty the line and then fail, losing the item.

     The [data-quickadd] handler that used to sit here bound to markup only the
     orphaned spine-nav section ever emitted, so it could never fire. */
  document.addEventListener('change', async (e) => {
    const select = e.target.closest('#bag [data-swap]');
    if (!select) return;
    const root = window.Shopify?.routes?.root || '/';
    const key = select.dataset.key;
    const quantity = Number(select.dataset.qty) || 1;
    const id = Number(select.value);
    if (!key || !id) return;

    const previous = select.querySelector('option[selected]')?.value;
    select.disabled = true;
    try {
      const added = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] }),
      }).then((r) => r.json());
      /* A successful add carries no `status`; a 422 carries status + description. */
      if (added?.status) throw new Error(added.description || 'That size is unavailable');

      const json = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname + window.location.search,
        }),
      }).then((r) => r.json());

      if (!applyDrawer(json?.sections?.['cart-drawer'])) {
        location.assign(`${root}cart`);
        return;
      }
      toast('SIZE UPDATED');
    } catch (err) {
      /* The add failed, so the cart is untouched — put the control back. */
      if (previous) select.value = previous;
      toast(String(err.message).toUpperCase());
    } finally {
      select.disabled = false;
    }
  });

  /* ------------------------------------------------------- pair products -- */
  const pair = $('#pair');
  if (pair) {
    const chosen = { base: null, object: null };

    const paint = () => {
      $$('.compose-option', pair).forEach((b) => {
        const on = chosen[b.dataset.slot]?.variant === b.dataset.variant;
        b.classList.toggle('active', on);
        b.setAttribute('aria-checked', String(on));
      });
      const both = chosen.base && chosen.object;
      $('#composeName').textContent = both
        ? `${chosen.base.code} + ${chosen.object.code}`
        : '—';
      $('#composePrice').textContent = both
        ? formatMoney(Number(chosen.base.price) + Number(chosen.object.price))
        : '—';
      $('#composeAdd').disabled = !both;
    };

    // Mirrors the shop's money format closely enough for a running subtotal;
    // the authoritative total always comes back from the cart.
    const formatMoney = (cents) => {
      const v = cents / 100;
      return `$${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)}`;
    };

    pair.addEventListener('click', (e) => {
      const b = e.target.closest('.compose-option');
      if (!b) return;
      chosen[b.dataset.slot] = { variant: b.dataset.variant, price: b.dataset.price, code: b.dataset.code };
      paint();
    });

    $('#composeAdd')?.addEventListener('click', async () => {
      if (!chosen.base || !chosen.object) return;
      const btn = $('#composeAdd');
      btn.disabled = true;
      try {
        await addToCart([
          { id: Number(chosen.base.variant), quantity: 1 },
          { id: Number(chosen.object.variant), quantity: 1 },
        ]);
        toast(`${chosen.base.code} + ${chosen.object.code} / ADDED`);
        openBag();
      } catch (err) {
        toast(String(err.message).toUpperCase());
      } finally {
        btn.disabled = false;
      }
    });

    // Preselect from ?base=&object= so the search shortcut can deep-link here.
    const params = new URLSearchParams(location.search);
    ['base', 'object'].forEach((slot) => {
      const want = params.get(slot);
      if (!want) return;
      const el = $$(`.compose-option[data-slot="${slot}"]`, pair)
        .find((b) => b.dataset.code?.toLowerCase() === want.toLowerCase() || b.dataset.variant === want);
      if (el) chosen[slot] = { variant: el.dataset.variant, price: el.dataset.price, code: el.dataset.code };
    });
    paint();
  }

  /* The price trace is gone. It was a twenty-four point random walk seeded from
     the variant id, drawn under a caption reading PRICE INDEX · INDICATIVE, on
     a store whose prices have no history to plot. Removing the markup left this
     a no-op; removing this leaves one fewer thing that looks like data. */

  /* Announcement bar dismissal, remembered per browser so it does not come
     back on the next page view. */
  const announce = $('.announce');
  if (announce) {
    const KEY = 'block_announce_hidden';
    try {
      if (localStorage.getItem(KEY) === '1') announce.hidden = true;
    } catch { /* private mode — leave it showing */ }

    $('#announceClose')?.addEventListener('click', () => {
      announce.hidden = true;
      try { localStorage.setItem(KEY, '1'); } catch { /* nothing to do */ }
    });
  }

  /* ------------------------------------------------------------- header -- */
  /* Top-level items reveal the directory tree. Hover on pointer devices, click
     everywhere, and the panels stack into an accordion once the bar collapses. */
  const nav = $('[data-nav]');
  if (nav) {
    const items = $$('.nav-item', nav);
    const isCompact = () => window.matchMedia('(max-width: 900px)').matches;

    const closeAll = (except) => {
      items.forEach((item) => {
        if (item === except) return;
        item.querySelector('.nav-trigger')?.setAttribute('aria-expanded', 'false');
        const panel = item.querySelector('.nav-panel');
        if (panel) panel.hidden = true;
      });
    };

    const setOpen = (item, open) => {
      const trigger = item.querySelector('.nav-trigger');
      const panel = item.querySelector('.nav-panel');
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      if (open) closeAll(item);
    };

    /* On a collection or product page the rail is a steady state, not a
       dropdown: Liquid renders one panel open — the one for the collection you
       are actually in — and hovering another menu item swaps the rail's contents
       rather than opening a second thing.

       So "closed" means something different there. Leaving the menu has to put
       the default back rather than empty a rail the page has already made room
       for; .stage is holding a 304px margin and an empty gutter is worse than
       the wrong branch. On the home page there is no default and this is a
       no-op, which is what leaves that page's hover behaviour untouched. */
    const railDefault = $('[data-rail-default]', nav);
    const restoreRail = () => {
      if (!railDefault || isCompact()) return;
      setOpen(railDefault, true);
    };

    /* The rail is a desktop idea. Liquid renders its default panel without
       `hidden` so the first painted frame is right up here — but on a phone that
       same panel would arrive as an already-expanded branch in the sheet, which
       is exactly what the client asked to stop. Close it below the breakpoint,
       and put it back if the window grows past it. */
    const syncRailToWidth = () => {
      if (!railDefault) return;
      if (isCompact()) setOpen(railDefault, false);
      else restoreRail();
    };
    syncRailToWidth();
    window.addEventListener('resize', syncRailToWidth);

    items.forEach((item) => {
      const trigger = item.querySelector('.nav-trigger');
      const panel = item.querySelector('.nav-panel');
      if (!trigger || !panel) return;

      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        setOpen(item, panel.hidden);
      });

      /* Hover is a convenience on pointer devices only — on touch it would fire
         alongside the click and immediately toggle back. */
      item.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || isCompact()) return;
        setOpen(item, true);
      });
      item.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse' || isCompact()) return;
        if (railDefault && item !== railDefault) restoreRail();
        else setOpen(item, false);
      });

      /* Keyboard: leaving the item entirely closes it. */
      item.addEventListener('focusout', (e) => {
        if (isCompact()) return;
        if (item.contains(e.relatedTarget)) return;
        if (railDefault && item !== railDefault) restoreRail();
        else setOpen(item, false);
      });
    });

    document.addEventListener('click', (e) => {
      if (nav.contains(e.target)) return;
      closeAll();
      restoreRail();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      closeAll();
      restoreRail();
      if (nav.hasAttribute('data-open')) toggleSheet(false);
    });

    /* Mobile: the whole bar becomes a full-screen sheet.

       The sheet covers the page, so keyboard focus must not be able to wander
       behind it into the content underneath — otherwise a tab key walks an
       invisible page while the sheet is still up. */
    const toggle = $('#navToggle');
    const FOCUSABLE =
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

    const trapFocus = (e) => {
      if (e.key !== 'Tab' || !nav.hasAttribute('data-open')) return;
      const fields = $$(FOCUSABLE, nav).filter((el) => el.offsetParent !== null);
      if (!fields.length) return;
      const first = fields[0];
      const last = fields[fields.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const toggleSheet = (open) => {
      nav.toggleAttribute('data-open', open);
      toggle?.setAttribute('aria-expanded', String(open));
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        /* Every branch closed. This used to open them all, and never did:
           setOpen calls closeAll, so each item shut the one before it and only
           the LAST branch ended up expanded — which is why MODULES was open and
           PLATFORMS was not, and nobody chose that. A tap expands one, and the
           accordion in setOpen closes the others, which is what the sheet
           should have been doing all along. */
        closeAll();
        document.addEventListener('keydown', trapFocus);
      } else {
        document.removeEventListener('keydown', trapFocus);
        toggle?.focus();
      }
    };
    toggle?.addEventListener('click', () => toggleSheet(!nav.hasAttribute('data-open')));

    /* ------------------------------------------------------ light / dark -- */
    /* The head has already stamped data-theme from storage before paint, so this
       only keeps the control in step and remembers the change. Deliberately not
       wired to prefers-color-scheme: the house ground is paper, and following
       the operating system would put most phone visitors into a mode nobody
       chose. The button is the whole switch. */
    /* Two instances — the corner and the phone sheet's foot — bound together, so
       they cannot disagree about which mode the site is in. */
    const themeToggles = $$('[data-theme-toggle]');
    if (themeToggles.length) {
      const THEME_KEY = 'block_v7_theme';
      const isDark = () => document.documentElement.getAttribute('data-theme') === 'dark';

      const setTheme = (dark) => {
        const root = document.documentElement;
        if (dark) root.setAttribute('data-theme', 'dark');
        else root.removeAttribute('data-theme');
        themeToggles.forEach((btn) => {
          btn.setAttribute('aria-pressed', String(dark));
          btn.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
        });
        try { localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light'); } catch { /* private mode */ }
      };

      setTheme(isDark());
      themeToggles.forEach((btn) => btn.addEventListener('click', () => setTheme(!isDark())));
    }

    /* The phone search icon opens the same sheet and lands the caret in the
       field, rather than introducing a second search surface to maintain. */
    $('#navSearchToggle')?.addEventListener('click', () => {
      toggleSheet(true);
      requestAnimationFrame(() => ($('#navSheetSearch') || $('#navSearch'))?.focus());
    });
    nav.addEventListener('click', (e) => {
      if (e.target.closest('a') && nav.hasAttribute('data-open')) toggleSheet(false);
    });
    window.addEventListener('resize', () => {
      if (!isCompact() && nav.hasAttribute('data-open')) toggleSheet(false);
    });
  }

  /* -------------------------------------------------------- search shortcut -- */
  /* Ports runFind()'s one genuinely useful trick: a query naming both a garment
     and a module jumps straight to the pairing tool instead of a result list.

     This was bound to #findForm, an id only the orphaned spine-nav section ever
     rendered, so it had stopped firing entirely. Delegated instead, so it works
     from the desktop bar and the phone sheet — and from any search field added
     later — rather than from one hard-coded id. */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[role="search"], #findForm');
    if (!form) return;
    const q = form.querySelector('input[name="q"], #findInput')?.value.trim().toLowerCase();
    if (!q) return;
    const wantsModule = /\btag|banner|badge|module\b/.test(q);
    const wantsGarment = /\btee|shirt|beanie|cap|jogger|garment\b/.test(q);
    if ((wantsModule && wantsGarment) || q.includes('+') || q.includes(' with ')) {
      e.preventDefault();
      location.href = `${window.Shopify?.routes?.root || '/'}pages/pair-products?q=${encodeURIComponent(q)}`;
    }
  });

  /* ----------------------------------------------------------------- faq -- */
  /* <details> already opens, closes and takes the keyboard on its own. The only
     thing it will not do is close its siblings, so that is all this adds — and
     if it never runs, every answer still opens. */
  $('[data-faq]')?.addEventListener('toggle', (e) => {
    const row = e.target;
    if (!row.matches('details') || !row.open) return;
    $$('details', e.currentTarget).forEach((other) => {
      if (other !== row) other.open = false;
    });
  }, true);

  /* -------------------------------------------------------------- system -- */
  /* The diagram. data-ready is set here, not in Liquid: it is what allows the
     CSS to hide the inactive panels, so without this script every description
     stays on the page instead of collapsing to one node's worth of nothing. */
  $$('[data-flow]').forEach((flow) => {
    const buttons = $$('[data-node]', flow);
    const panels = $$('[data-panel]', flow);
    if (!buttons.length || !panels.length) return;

    const open = (i) => {
      buttons.forEach((b, n) => b.setAttribute('aria-expanded', String(n === i)));
      panels.forEach((pnl, n) => pnl.toggleAttribute('data-open', n === i));
    };

    buttons.forEach((b, i) => {
      b.addEventListener('click', () => open(i));
      /* Hover is a convenience for a mouse only — on touch it fires alongside
         the click and immediately toggles back. */
      b.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') open(i);
      });
      b.addEventListener('keydown', (e) => {
        const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        const next = (i + step + buttons.length) % buttons.length;
        open(next);
        buttons[next].focus();
      });
    });

    open(0);
    flow.setAttribute('data-ready', '');
  });

  /* ------------------------------------------------------------ manifesto -- */
  /* The digital scramble from the client's about.html, made the entrance.

     The page opens on noise: every line appears as random characters and
     resolves into its words left to right, one line after the next, all within
     about a second. From then on something is always moving: three of the
     bracketed words are scrambling at any moment, and as each one resolves
     another, somewhere else in the text, takes its place. Hover flips a word too.

     Before this file runs, site.css keeps the lines hidden, with a late CSS
     fade as the fallback, so the real text is never painted and then scrambled
     on top of. data-live is the handover. Reduced motion never sets it, and gets
     the text still.

     The type is monospaced, so a random character is exactly as wide as the one
     it stands in for and a line never reflows mid-scramble. The words' widths
     are pinned anyway, for any fallback font that is not. */
  $$('[data-memo]').forEach((memo) => {
    const lines = $$('.line', memo);
    if (!lines.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\<>[]{}=+*#%$&@_-';
    const noise = () => CHARS[(Math.random() * CHARS.length) | 0];
    const FRAME = 32;
    const words = $$('.g', memo);
    words.forEach((el) => { el.dataset.text = el.textContent; });

    const lockWidths = () => {
      words.forEach((el) => {
        if (el._busy) return;
        el.style.minWidth = '';
        el.style.minWidth = `${Math.ceil(el.getBoundingClientRect().width)}px`;
      });
    };

    /* One resolve, over any set of text nodes read as a single run of
       characters: a whole line (its bracketed word included) for the entrance, or
       one word afterwards. Each character settles on its own frame, later the
       further along it sits, with jitter so the edge is ragged. */
    const resolve = (nodes, { spread, jitter, lead }, done) => {
      const texts = nodes.map((n) => n.nodeValue);
      let offset = 0;
      const ends = texts.map((t) => {
        const row = Array.from(t, (c, i) => lead + (offset + i) * spread + Math.random() * jitter);
        offset += t.length;
        return row;
      });
      let frame = 0;
      let last = 0;
      const paint = () => {
        let settled = true;
        nodes.forEach((node, k) => {
          const t = texts[k];
          let out = '';
          for (let i = 0; i < t.length; i++) {
            if (t[i] === ' ' || frame >= ends[k][i]) out += t[i];
            else { out += noise(); settled = false; }
          }
          node.nodeValue = out;
        });
        return settled;
      };
      paint();
      const tick = (now) => {
        if (now - last < FRAME) { requestAnimationFrame(tick); return; }
        last = now;
        frame++;
        if (paint()) {
          nodes.forEach((node, k) => { node.nodeValue = texts[k]; });
          done?.();
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const textNodes = (root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const out = [];
      while (walker.nextNode()) if (walker.currentNode.nodeValue) out.push(walker.currentNode);
      return out;
    };

    /* The pace varies per flip, so the text never ticks over in step. */
    const scramble = (el, done) => {
      if (el._busy) return false;
      el._busy = true;
      el.classList.add('on');
      el.textContent = el.dataset.text;
      resolve(textNodes(el), { spread: 1 + Math.random() * 1.1, jitter: 5 + Math.random() * 6, lead: 4 }, () => {
        el.classList.remove('on');
        el._busy = false;
        done?.();
      });
      return true;
    };

    /* Always in motion. This used to give each word its own random timer of
       three to twelve seconds, which left whole seconds where nothing moved at
       all. Now a fixed number are kept running: whenever one resolves, another
       word starts a moment later. The word that just finished is passed over,
       so the movement travels around the text instead of staying in one place. */
    const ACTIVE = Math.min(3, words.length);
    let running = 0;
    let lastWord = null;
    const launch = () => {
      if (document.visibilityState !== 'visible') return;
      while (running < ACTIVE) {
        const idle = words.filter((el) => !el._busy);
        const fresh = idle.filter((el) => el !== lastWord);
        const pool = fresh.length ? fresh : idle;
        if (!pool.length) return;
        const el = pool[(Math.random() * pool.length) | 0];
        lastWord = el;
        running += 1;
        scramble(el, () => {
          running -= 1;
          setTimeout(launch, 40 + Math.random() * 260);
        });
      }
    };
    /* A hidden tab stops painting, so nothing new is started there; the words
       already mid-flip finish when it is looked at again, and this tops the
       count back up. */
    document.addEventListener('visibilitychange', launch);

    words.forEach((el) => {
      el.addEventListener('mouseenter', () => scramble(el));
    });

    /* The entrance. Lines start 60ms apart and each resolves in well under a
       second, so the whole manifesto has decoded before a visitor has read the
       first line — the noise is the first thing they see, not the words. The
       flipping takes over in the same frame the last line lands, with no gap. */
    let played = false;
    const enter = () => {
      if (played) return;
      played = true;
      words.forEach((el) => { el._busy = true; });
      let remaining = lines.length;
      const finish = () => {
        lines.forEach((line) => line.setAttribute('data-in', ''));
        words.forEach((el) => {
          el.textContent = el.dataset.text;
          el._busy = false;
        });
        lockWidths();
        launch();
      };
      lines.forEach((line, k) => {
        setTimeout(() => {
          /* resolve() reads the real words first and paints its opening frame
             of noise synchronously, so the line is only shown after that. */
          resolve(textNodes(line), { spread: 0.3, jitter: 7, lead: 2 }, () => {
            remaining -= 1;
            if (!remaining) finish();
          });
          line.setAttribute('data-in', '');
        }, k * 60);
      });
    };

    /* A tab opened in the background has nobody watching, so the entrance waits
       until it is looked at. */
    memo.setAttribute('data-live', '');
    lockWidths();
    if (document.visibilityState === 'visible') enter();
    else document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') enter();
    });
    document.fonts?.ready.then(lockWidths);

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(lockWidths, 150);
    }, { passive: true });
  });

  /* ------------------------------------------------------------ carousel -- */
  /* The row already scrolls by touch, trackpad and keyboard — CSS scroll-snap
     does the work. These only add arrows for a mouse, and disable themselves at
     each end so they never look live when they are not. */
  $$('[data-carousel]').forEach((carousel) => {
    const track = $('[data-carousel-track]', carousel);
    const prev = $('[data-carousel-prev]', carousel);
    const next = $('[data-carousel-next]', carousel);
    if (!track || !prev || !next) return;

    const step = () => track.querySelector(':scope > *')?.getBoundingClientRect().width || track.clientWidth;

    const paint = () => {
      const max = track.scrollWidth - track.clientWidth;
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= max - 1;
      carousel.querySelector('.carousel-arrows')?.toggleAttribute('hidden', max <= 1);
    };

    const nudge = (dir) => {
      track.scrollBy({
        left: dir * (step() + 22),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    prev.addEventListener('click', () => nudge(-1));
    next.addEventListener('click', () => nudge(1));
    track.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint);
    paint();
  });

  /* ------------------------------------------------------------- product -- */
  /* Everything here is written to be re-run: choosing a size re-renders the
     product section from the server and swaps it in, so the bindings have to be
     re-made against the new nodes rather than assumed to survive. */
  const bindPdp = () => {
    const pdp = $('[data-pdp]');
    if (!pdp) return;

    /* --- gallery position ---------------------------------------------- */
    const shots = $$('[data-shot]', pdp);
    const dots = $$('[data-dot]', pdp);
    const track = $('[data-gallery]', pdp);
    const progress = $('[data-progress] span', pdp);

    if (shots.length > 1) {
      /* Which shot is under the middle of the screen.

         This was an IntersectionObserver at threshold 0.55, and it never fired
         on desktop: a 3:4 frame is most of a screen tall, so 55% of one shot is
         never on screen at once and the indicator sat on the first dot for the
         whole page. Nearest-centre always has an answer — at every scroll
         position, at every frame height, and on both axes, so the same code
         serves the vertical column and the phone's sideways gallery.

         Dots are matched to shots by position rather than by data-shot. They
         are rendered from the same filtered loop, so position IS the mapping,
         and it cannot drift the way two separately-counted indices can. */
      const paintDots = () => {
        const midX = window.innerWidth / 2;
        const midY = window.innerHeight / 2;
        let near = 0;
        let best = Infinity;
        shots.forEach((shot, n) => {
          const r = shot.getBoundingClientRect();
          const d = Math.hypot((r.left + r.right) / 2 - midX, (r.top + r.bottom) / 2 - midY);
          if (d < best) { best = d; near = n; }
        });
        dots.forEach((dot, n) => {
          if (n === near) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      /* One paint per frame at most — this runs on every scroll event. */
      let queued = false;
      const schedule = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; paintDots(); paintProgress(); });
      };

      const paintProgress = () => {
        if (!track || !progress) return;
        const span = track.scrollWidth - track.clientWidth;
        const pct = span > 0 ? (track.scrollLeft / span) * 100 : 0;
        progress.style.width = `${Math.max(100 / shots.length, pct)}%`;
      };

      /* Rebound on every render, so the previous section's listeners are taken
         off first — bindPdp runs again after each variant swap. */
      pdp._offScroll?.();
      const opts = { passive: true };
      window.addEventListener('scroll', schedule, opts);
      window.addEventListener('resize', schedule, opts);
      track?.addEventListener('scroll', schedule, opts);
      pdp._offScroll = () => {
        window.removeEventListener('scroll', schedule, opts);
        window.removeEventListener('resize', schedule, opts);
        track?.removeEventListener('scroll', schedule, opts);
      };

      dots.forEach((dot, n) => {
        dot.addEventListener('click', () => {
          shots[n]?.scrollIntoView({
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'center',
            inline: 'center',
          });
        });
      });

      paintDots();
      paintProgress();
    }

    /* --- tabs ------------------------------------------------------------ */
    /* data-ready is set here, never in Liquid: if this never runs, all three
       panels stay open and labelled instead of collapsing to nothing. */
    const tabs = $('[data-tabs]', pdp);
    if (tabs) {
      const buttons = $$('[role="tab"]', tabs);
      const panels = $$('[role="tabpanel"]', tabs);
      const line = $('[data-tabline]', tabs);
      let current = 0;

      /* The rule under the row is continuous and the mark slides along it,
       * rather than an underline switching from one label to the next. Measured
       * from the button itself so it tracks whatever the labels happen to be. */
      const paintLine = () => {
        if (!line) return;
        const b = buttons[current];
        if (!b) return;
        line.style.width = b.offsetWidth + 'px';
        line.style.transform = 'translateX(' + b.offsetLeft + 'px)';
      };

      const select = (i) => {
        current = i;
        buttons.forEach((b, n) => {
          b.setAttribute('aria-selected', String(n === i));
          b.tabIndex = n === i ? 0 : -1;
        });
        panels.forEach((pnl, n) => { pnl.hidden = n !== i; });
        paintLine();
      };
      buttons.forEach((b, i) => {
        b.addEventListener('click', () => select(i));
        b.addEventListener('keydown', (e) => {
          const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
          if (!step) return;
          e.preventDefault();
          const next = (i + step + buttons.length) % buttons.length;
          select(next);
          buttons[next].focus();
        });
      });
      select(0);
      tabs.setAttribute('data-ready', '');
      /* data-ready flips the row to display:flex, so the first measurement has
       * to happen after that or every button reads offsetWidth 0. */
      requestAnimationFrame(paintLine);
      addEventListener('resize', paintLine, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(paintLine);
    }

  };

  /* --- choosing a size or colour, without losing the page ---------------- */
  /* These are real ?variant= links, so they work with JavaScript off. With it,
     the product section is re-rendered server-side and swapped in: price,
     availability, the sold-out state of every size and the button's own
     disabled attribute all come back computed by Shopify, so nothing here has
     to reimplement variant logic and get it subtly wrong. */
  let pending;
  document.addEventListener('click', async (e) => {
    const link = e.target.closest('[data-variant-link]');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    const pdp = $('[data-pdp]');
    if (!pdp?.dataset.sectionId) return;

    const url = new URL(link.href, window.location.origin);
    const variant = url.searchParams.get('variant');
    if (!variant) return;
    e.preventDefault();

    pending?.abort();
    pending = new AbortController();
    const signal = pending.signal;
    pdp.setAttribute('data-loading', '');
    try {
      const res = await fetch(`${url.pathname}?variant=${variant}&section_id=${pdp.dataset.sectionId}`, {
        signal,
      });
      if (!res.ok) throw new Error(`section render returned ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

      /* A slower earlier click must not land on top of a later one. abort()
         usually catches this, but the response can already be in flight. */
      if (signal.aborted) return;

      const freshPanel = doc.querySelector('.pdp-panel-inner');
      const livePanel = $('.pdp-panel-inner', pdp);
      if (!freshPanel || !livePanel) throw new Error('no .pdp-panel-inner in the rendered section');

      /* The buy row is carried across the swap rather than re-rendered with the
         rest of the panel.

         PreProduct is a global app embed with no markup in this theme. It finds
         the buy button once, at page load, hides it and puts its own in front.
         Replacing the panel wholesale destroyed both, and it has no way back:
         locationchange, PPshowPreProduct() and adding its attributes afterwards
         were all tried against the live page and none rebind it — its observers
         are wired at load, to the element that was there at load. The result was
         a pre-order product with a live ADD TO BAG on it.

         So the element survives, and the values move instead. data-starting-
         variant is the one PreProduct documents itself as watching; the rest is
         what the server would have rendered anyway. */
      const liveBuy = $('.buy-row', livePanel);
      const freshBuy = freshPanel.querySelector('.buy-row');
      livePanel.innerHTML = freshPanel.innerHTML;

      if (liveBuy && freshBuy) {
        const placeholder = $('.buy-row', livePanel);
        placeholder?.replaceWith(liveBuy);

        const copy = (sel, fn) => {
          const from = freshBuy.querySelector(sel);
          const to = liveBuy.querySelector(sel);
          if (from && to) fn(to, from);
        };
        copy('[name="id"]', (to, from) => { to.value = from.value; });
        copy('.pdp-add-price', (to, from) => { to.textContent = from.textContent; });
        copy('[data-buy] > span:first-child', (to, from) => { to.textContent = from.textContent; });
        /* Availability is Shopify's answer, not ours — take it as rendered. */
        copy('[data-buy]', (to, from) => {
          if (from.hasAttribute('disabled')) to.setAttribute('disabled', '');
          else to.removeAttribute('disabled');
          /* Last, and the point of all of the above. */
          const variantId = from.getAttribute('data-starting-variant');
          if (variantId) to.setAttribute('data-starting-variant', variantId);
        });
      }

      /* Only touch the photography when it actually changed — a size change
         keeps the same shots, and rebuilding them would throw away the
         shopper's place in the gallery. */
      const freshMedia = doc.querySelector('.pdp-media');
      const liveMedia = $('.pdp-media', pdp);
      const firstSrc = (root) => root?.querySelector('img')?.getAttribute('src') || '';
      if (freshMedia && liveMedia && firstSrc(freshMedia) !== firstSrc(liveMedia)) {
        const top = $('[data-gallery]', liveMedia)?.scrollTop || 0;
        liveMedia.innerHTML = freshMedia.innerHTML;
        /* Hold the loading state until the incoming lead shot has actually
           decoded. Without it the gallery is emptied and repainted while the
           panel beside it has already updated, so a colour change reads as a
           stutter rather than a swap — every img is a new element and shots 2..n
           are lazy, so they arrive after layout. */
        const lead = liveMedia.querySelector('img');
        if (lead && lead.decode) { try { await lead.decode(); } catch { /* cached or aborted */ } }
        /* Same colourway count either way, so keep the shopper's place. */
        const track = $('[data-gallery]', liveMedia);
        if (track && top) track.scrollTop = top;
      }

      window.history.replaceState({}, '', `${url.pathname}?variant=${variant}`);
      bindPdp();
      /* The header watches the first shot, and the swap above just detached the
         one it was holding — so it has to be told, here, where the replacement
         happened. It cannot be told from the gallery's scroll handler: that fires
         only when something scrolls, and a colour click at the top of the page
         scrolls nothing. It fired from there until now, which is why the bar went
         solid over shot 1 on every colour change and stayed that way until the
         shopper happened to scroll. The observer needs no help while scrolling —
         reporting against the viewport whatever moved the element is the whole
         reason it is an observer — so this is the only moment it needs. */
      document.dispatchEvent(new Event('pdp:rebound'));
      paintWatch();
      loadRecommendations();
    } catch (err) {
      if (err.name === 'AbortError') return;
      /* Last resort only. Showing the previous variant's price and availability
         under a newly-selected size is worse than a reload, so the navigation
         stays — but it is a real failure, not a routine path, and it says so
         rather than reloading silently and looking like the design. */
      console.warn('[pdp] variant swap fell back to a full page load:', err.message);
      window.location.assign(link.href);
    } finally {
      pdp.removeAttribute('data-loading');
    }
  });

  /* --- complete the look ------------------------------------------------- */
  /* `recommendations` is empty on a normal page load; it is only populated when
     a section is fetched through the recommendations endpoint. So the shell
     ships empty and is filled here, and stays empty when there is nothing to
     show rather than printing a heading over an empty row. */
  let recLoaded = null;
  function loadRecommendations() {
    const rec = $('[data-recommendations]');
    if (!rec || !rec.dataset.url || !rec.dataset.productId) return;
    if (recLoaded === rec.dataset.productId) return;
    recLoaded = rec.dataset.productId;

    const params = new URLSearchParams({
      section_id: rec.dataset.sectionId || '',
      product_id: rec.dataset.productId,
      limit: rec.dataset.limit || '4',
      intent: rec.dataset.intent || 'related',
    });
    fetch(`${rec.dataset.url}?${params}`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((text) => {
        const fresh = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          rec.innerHTML = fresh.innerHTML;
          paintWatch();
        }
      })
      .catch(() => {
        /* A store with no order history returns none, and complementary returns
           none until the merchant sets them. Empty is a normal answer. */
      });
  }

  bindPdp();
  loadRecommendations();

  paintWatch();
});

/* ----------------------------------------------------------------------------
 * Backdrop matching — deliberately its own scope.
 *
 * Every image frame on the site is object-fit:contain, so nothing is ever
 * cropped or stretched, whatever shape is uploaded. That leaves a band wherever
 * the photograph's proportions differ from the frame's.
 * snippets/shot-backdrop.liquid paints that band for the files measured at
 * build time; this covers everything else — anything uploaded from here on — by
 * sampling the picture's own corners once it has decoded.
 *
 * It sits outside the main IIFE on purpose: an unrelated error in theme
 * behaviour must never be able to leave images sitting in the wrong colour.
 *
 * Shopify's CDN sends Access-Control-Allow-Origin:*, so the canvas stays
 * untainted. If a read throws anyway the frame keeps its --tile-bg default and
 * the image is still uncropped — the fallback is cosmetic, never broken.
 * -------------------------------------------------------------------------- */
part('backdrop matching', () => {
  const SEL = '.bleed-img,.card-img,.hero-img img,.shot img,.archive-images img,' +
              '.compose-option img,.bag-tile img,.product-secondary img,' +
              '.product-primary img,.pdp-shot-img';
  const FRAME = '.card-media,.bleed-frame,.hero-img,.shot,.archive-images a,' +
                '.compose-option,.bag-tile,.product-secondary,.product-primary,' +
                '.pdp-shot';

  const avgPatch = (img, sx, sy, sw, sh) => {
    const n = 4;
    const c = document.createElement('canvas');
    c.width = c.height = n;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    try {
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, n, n);
      const d = ctx.getImageData(0, 0, n, n).data;
      let r = 0, g = 0, b = 0, px = 0;
      for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; px++; }
      return [r / px, g / px, b / px];
    } catch { return null; }
  };

  const rgb = (a) => `rgb(${Math.round(a[0])},${Math.round(a[1])},${Math.round(a[2])})`;
  const mix = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];

  /* Three colours, not one. A contained image in a taller frame touches the pad
   * along its TOP and BOTTOM edges, so each band has to match the edge it meets
   * — one average for the whole picture paints a hard bar across any shot whose
   * backdrop is not uniform. headwear-duo-model-black runs #0d0d0d at the top
   * and #c1bdba at the bottom; the sku10 gallery does the same. Measured, those
   * two are off by 263 and 117 (summed RGB) against a single corner average,
   * where anything over 25 is a line you can see.
   *
   * Sample the outer eighth of each edge only: a model centred in frame reaches
   * the top edge, but the corners of that edge are still backdrop. */
  const backdropOf = (img) => {
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) return null;
    const band = Math.max(2, Math.round(w * 0.12));
    const depth = Math.max(2, Math.round(h * 0.006));
    const xr = Math.max(0, w - band);
    const yb = Math.max(0, h - depth);

    /* From the true edge, row 0 and row h-1. The pad butts against the shot's
     * outermost row, so that is the row it has to match — an inset steps over a
     * thin edge band and pads the wrong colour. */
    const tl = avgPatch(img, 0, 0, band, depth);
    const tr = avgPatch(img, xr, 0, band, depth);
    const bl = avgPatch(img, 0, yb, band, depth);
    const br = avgPatch(img, xr, yb, band, depth);
    if (!tl || !tr || !bl || !br) return null;

    const top = mix(tl, tr), bottom = mix(bl, br);
    return { top: rgb(top), bottom: rgb(bottom), flat: rgb(mix(top, bottom)) };
  };

  /* The displayed <img> has no crossorigin attribute, so reading it would taint
   * the canvas even though the CDN allows the read. Sample a CORS-enabled proxy
   * of the same URL instead — the browser serves it from cache — rather than
   * putting crossorigin on nine separate image_tag call sites. */
  const match = (img) => {
    const frame = img.closest(FRAME) || img.parentElement;
    if (!frame || frame.dataset.shotBg) return;
    if (frame.style.getPropertyValue('--shot-bg')) { frame.dataset.shotBg = '1'; return; }
    const src = img.currentSrc || img.src;
    if (!src) return;
    frame.dataset.shotBg = '1';                 // claim it once; never re-enter
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = () => {
      const bg = backdropOf(probe);
      if (!bg) return;
      frame.style.setProperty('--shot-bg', bg.flat);
      frame.style.setProperty('--shot-bg-top', bg.top);
      frame.style.setProperty('--shot-bg-bot', bg.bottom);
    };
    probe.onerror = () => {};                   // keep the --tile-bg default
    probe.src = src;
  };

  const scan = (root) => {
    (root || document).querySelectorAll(SEL).forEach((img) => {
      if (img.complete && img.naturalWidth) match(img);
      else img.addEventListener('load', () => match(img), { once: true });
    });
  };

  scan();
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
});

/* ----------------------------------------------------------------------------
 * The header over the hero.
 *
 * The picture runs to the top of the frame and the bar sits on it with no
 * ground of its own, so the type has to be chosen from whatever is behind it.
 * sections/hero-image decides that in Liquid from the measured backdrop, which
 * means the first painted frame is already right; this refines it from the
 * photograph for the two cases Liquid cannot see — a hero set to fill the
 * frame, where the backdrop band is not what is under the bar, and any image
 * uploaded after scripts/build-backdrops.py last ran.
 *
 * Its own scope, like the backdrop matcher below it: a header stuck in the
 * wrong colour is a blemish, and it must not be able to take navigation with it.
 *
 * Nothing here is load-bearing any more. The overlay itself — the hero pulled up
 * under the bar, the bar transparent, cream type over a dark shot — is a :has()
 * rule in site.css, decided from the markup in the first painted frame. This
 * only refines the tone from the photograph, and hands the bar its ground back
 * once the hero has scrolled past. If it never runs, the page is still right at
 * the top, which is the half that used to be lost entirely.
 * -------------------------------------------------------------------------- */
part('header over the hero', () => {
  const nav = document.getElementById('siteNav');
  const hero = document.querySelector('[data-hero-overlay]');
  if (!nav || !hero) return;

  /* Only a hero that OPENS the page. One further down would put the bar over
     the middle of the document and pull the layout up by a header's height.

     Asked of the first SECTION rather than the first child, because the two
     stopped being the same thing: the live page has something in .view that the
     theme does not put there, and the strict version of this test — along with
     the stylesheet's — silently switched the overlay off on a phone. A node
     injected ahead of the first section no longer counts as the first section. */
  const view = document.querySelector('.view');
  const firstSection = view?.querySelector(':scope > .shopify-section') || view?.firstElementChild;
  const heroSection = hero.closest('.shopify-section') || hero;
  if (firstSection && firstSection !== heroSection && !firstSection.contains(hero)) return;

  /* Shopify's own color_brightness weighting, so the threshold here and the
     one in Liquid agree rather than disagreeing near the midpoint. */
  const DARK = 135;
  const brightness = (r, g, b) => (r * 299 + g * 587 + b * 114) / 1000;

  const parse = (value) => {
    const v = (value || '').trim();
    const hex = v.match(/^#?([0-9a-f]{6})$/i);
    if (hex) {
      const n = parseInt(hex[1], 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    }
    const rgb = v.match(/^rgba?\(([^)]+)\)$/i);
    if (rgb) {
      const parts = rgb[1].split(',').map((x) => parseFloat(x));
      if (parts.length >= 3 && parts.every(Number.isFinite)) return parts.slice(0, 3);
    }
    return null;
  };

  /* Both classes, not one toggled class. Liquid may already have said "dark" on
     the body through the tone attribute; leaving nav--on-dark merely absent is
     not the same as saying "light", and would let the server's guess stand after
     the photograph has been read and disagreed with it. */
  const applyTone = (tone) => {
    nav.classList.toggle('nav--on-dark', tone === 'dark');
    nav.classList.toggle('nav--on-light', tone !== 'dark');
  };
  const applyColour = (value) => {
    const rgb = parse(value);
    if (rgb) applyTone(brightness(...rgb) < DARK ? 'dark' : 'light');
  };

  applyTone(hero.dataset.heroTone);

  /* Transparent only while the hero is still under the bar.
   *
   * The marker is re-queried every paint rather than captured once. On a product
   * page a variant swap replaces .pdp-media's innerHTML, which detaches the shot
   * this closure was holding — a detached node reports an all-zero rect, so the
   * comparison went false forever, the bar stuck solid, and the incoming shot's
   * tone was never applied. This IIFE runs at parse and nothing re-runs it, so
   * the lookup has to happen here. */
  /* An observer, not a scroll handler.
   *
   * This used to be a rect comparison on every window scroll. That worked while
   * the page was the only thing that scrolled — but on desktop the gallery is
   * its own scroll port now, and the first shot leaves by the gallery scrolling,
   * which the window never hears. A scroll event does not bubble either, and the
   * gallery is replaced wholesale on a colour swap, so a listener bound to it
   * does not survive.
   *
   * An IntersectionObserver has none of those problems: it reports against the
   * viewport whatever moved the element, it needs no event, and re-observing
   * after a swap is one call. rootMargin pulls the top of the root down to the
   * bottom of the bar, so "still intersecting" means exactly "still under the
   * bar", which is the question being asked.
   *
   * Not the threshold:0.55 observer this replaced years ago and that failed — a
   * shot is most of a screen tall, so a fraction of one is never on screen at
   * once. Threshold 0 against a margin asks a different question and answers it. */
  /* Two questions, two observers, because they stopped having the same answer.
   *
   *   Is anything still under the bar?   -> whether the bar keeps its transparent
   *                                         ground, asked of the whole gallery
   *   Which shot is under the bar?       -> which colour its type takes, asked of
   *                                         the shots individually
   *
   * They used to be one question asked of the first shot, and that was right
   * while the bar went solid the moment shot 1 left. The gallery is its own
   * sticky scroll port now: scroll it to shot 4 and shot 1 is long gone while
   * the photography still fills the screen under the bar — measured on the live
   * page, the bar went solid at the top of the document and did not come back
   * until the gallery was scrolled back to the start. The ground question has to
   * be asked of .pdp-media, which is what is actually under there.
   *
   * The second observer is what that costs. A bar that stays transparent across
   * the whole run has to know what it is standing on, because the shots
   * alternate dark and light and cream type on #f2f2f2 is invisible. rootMargin
   * closes the root down to a one-pixel strip at the bottom edge of the bar, so
   * the only shot intersecting is the one crossing that line. Every shot carries
   * data-hero-tone now; main-product.liquid says why.
   *
   * .pdp-media survives a colour swap — innerHTML replaces its children, not the
   * element — so the ground observer holds. The shots inside do not, which is
   * what retarget is for. */
  /* Liquid answers when the backdrop lookup knows the image, and says nothing
     when it does not — 27 of the catalogue's shots are not in it. The sampler in
     the next part of this file writes --shot-bg-top onto the same element a
     moment later, so read that as the second source and stay quiet if neither
     has an answer. */
  const toneOf = (el) => {
    if (el.dataset.heroTone) return el.dataset.heroTone;
    const rgb = parse(getComputedStyle(el).getPropertyValue('--shot-bg-top'));
    return rgb ? (brightness(...rgb) < DARK ? 'dark' : 'light') : null;
  };

  let current = hero;
  let io;
  let toneIo;

  const observe = () => {
    io?.disconnect();
    toneIo?.disconnect();
    const bar = Math.ceil(nav.getBoundingClientRect().bottom);

    io = new IntersectionObserver(
      ([entry]) => nav.classList.toggle('nav--past', !entry.isIntersecting),
      { rootMargin: `-${bar}px 0px 0px 0px`, threshold: 0 }
    );
    io.observe(document.querySelector('.pdp-media') || current);

    /* One shot needs no tracking — its tone is applied once and never changes.
       The home page hero lands here too, and keeps the behaviour it has. */
    const toned = [...new Set([...document.querySelectorAll('.pdp-shot, [data-hero-tone]')])];
    if (toned.length < 2) return;
    const below = Math.max(0, window.innerHeight - bar - 1);
    toneIo = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const tone = toneOf(entry.target);
          /* null means nobody knows yet. Leave the bar as it is rather than
             assert light, which is dark ink and the unreadable half of the pair. */
          if (tone) applyTone(tone);
        }
      },
      { rootMargin: `-${bar}px 0px -${below}px 0px`, threshold: 0 }
    );
    toned.forEach((shot) => toneIo.observe(shot));
  };

  /* The marker is re-read rather than held: a variant swap replaces .pdp-media's
     innerHTML, which detaches the shot this closure was watching. */
  const retarget = () => {
    const live = document.querySelector('[data-hero-overlay]');
    if (!live || live === current) return;
    current = live;
    applyTone(current.dataset.heroTone);
    observe();
  };

  /* Start where the stylesheet left it — over the hero — and let the observer
     take the ground back when the hero has gone. The reverse of what this did
     before, and the reason is that the assumed state has moved: the transparent
     bar is now painted by CSS before this file is even fetched, so the thing
     that must not be guessed is the OTHER one. If the observer never reports,
     the top of the page is still right; only the bar over the feed below it is
     wrong, where before the whole effect was lost. In practice the first
     callback arrives immediately on observe. */
  nav.classList.remove('nav--past');
  observe();
  window.addEventListener('resize', observe, { passive: true });
  document.addEventListener('pdp:rebound', retarget);

  const frame = hero.querySelector('.bleed-frame');
  const img = frame?.querySelector('img');
  if (!frame || !img) return;

  /* Under a film the bar stands on the grade, not on the poster, and Liquid has
     already said dark. Sampling the poster could only disagree with what is
     actually on screen once the film is playing. */
  if (hero.hasAttribute('data-hero-film')) return;

  if (!hero.classList.contains('bleed--cover')) {
    /* Contained: the band behind the bar IS the backdrop, so follow it. It may
       be set by Liquid already, or written later by the backdrop matcher — the
       observer covers both without polling. */
    applyColour(frame.style.getPropertyValue('--shot-bg'));
    new MutationObserver(() => applyColour(frame.style.getPropertyValue('--shot-bg')))
      .observe(frame, { attributes: true, attributeFilter: ['style'] });
    return;
  }

  /* Filling the frame: read the strip of the photograph the bar actually
     covers. Sampled through a CORS-enabled copy of the same URL, served from
     cache — the displayed <img> carries no crossorigin attribute and would
     taint the canvas even though Shopify's CDN allows the read. */
  const sampleTop = (source) => {
    const w = source.naturalWidth;
    const h = source.naturalHeight;
    if (!w || !h) return;
    const band = Math.max(1, Math.round(h * 0.16));
    const c = document.createElement('canvas');
    c.width = 24;
    c.height = 6;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    try {
      ctx.drawImage(source, 0, 0, w, band, 0, 0, 24, 6);
      const d = ctx.getImageData(0, 0, 24, 6).data;
      let r = 0, g = 0, b = 0, px = 0;
      for (let i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; px++; }
      applyTone(brightness(r / px, g / px, b / px) < DARK ? 'dark' : 'light');
    } catch {
      /* Keep the tone Liquid worked out. */
    }
  };

  const src = img.currentSrc || img.src;
  if (!src) return;
  const probe = new Image();
  probe.crossOrigin = 'anonymous';
  probe.decoding = 'async';
  probe.onload = () => sampleTop(probe);
  probe.onerror = () => {};
  probe.src = src;
});

/* ----------------------------------------------------------------------------
 * The hero film.
 *
 * The <video> arrives with no src, so nothing about it can slow the page. The
 * poster is the hero image, already prioritised and already the LCP element. A
 * file is handed over only once:
 *
 *   - the load event has fired and the browser is idle, so the film never
 *     competes with the image, the stylesheet or the fonts
 *   - the hero is on screen, so a visitor who has already scrolled past downloads
 *     nothing
 *   - nothing has asked for less: reduced motion, data saver, or a connection
 *     the browser rates as 3G or slower all keep the still
 *
 * A phone gets the 720p rendition, a desktop the 1080p one. The film fades in
 * on `playing`, not on load, so a stall shows the poster rather than black. It
 * pauses off screen and in a background tab, and resumes only if the visitor
 * did not pause it themselves.
 *
 * Its own scope: a film that fails must leave the still, not break the page.
 * -------------------------------------------------------------------------- */
part('hero film', () => {
  const hero = document.querySelector('[data-hero-film]');
  const video = hero?.querySelector('[data-hero-video]');
  if (!hero || !video) return;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const conn = navigator.connection;
  if (reduce.matches) return;
  if (conn?.saveData) return;
  if (/^(slow-2g|2g|3g)$/.test(conn?.effectiveType || '')) return;

  const src = window.matchMedia('(max-width: 900px)').matches
    ? video.dataset.srcM || video.dataset.src
    : video.dataset.src || video.dataset.srcM;
  if (!src) return;

  const toggle = hero.querySelector('[data-hero-film-toggle]');
  const label = toggle?.querySelector('[data-label]');
  const glyph = toggle?.querySelector('.bleed-film-glyph');

  let ready = false;       // the page has loaded and gone idle
  let started = false;     // the file has been handed over
  let visible = false;
  let userPaused = false;

  const sync = () => {
    const playing = !video.paused;
    hero.toggleAttribute('data-paused', !playing);
    if (!toggle) return;
    toggle.setAttribute('aria-pressed', String(playing));
    toggle.setAttribute('aria-label', playing ? 'Pause the film' : 'Play the film');
    if (label) label.textContent = playing ? 'PAUSE' : 'PLAY';
    if (glyph) glyph.textContent = playing ? 'Ⅱ' : '▷';
  };

  const play = () => {
    if (!started) return;
    const attempt = video.play();
    attempt?.catch((err) => {
      /* Refused rather than interrupted — iOS Low Power Mode, or a browser
         autoplay policy. The poster stays, and PLAY is offered instead. */
      if (err?.name === 'NotAllowedError' && toggle) toggle.hidden = false;
      sync();
    });
  };

  const maybeStart = () => {
    if (started || !ready || !visible || document.hidden) return;
    started = true;
    video.muted = true;
    video.defaultMuted = true;
    video.preload = 'auto';
    video.src = src;
    play();
  };

  video.addEventListener('playing', () => {
    hero.setAttribute('data-playing', '');
    if (toggle) toggle.hidden = false;
    sync();
  });
  video.addEventListener('play', sync);
  video.addEventListener('pause', sync);
  video.addEventListener('error', () => {
    hero.removeAttribute('data-playing');
    if (toggle) toggle.hidden = true;
  });

  toggle?.addEventListener('click', () => {
    if (video.paused) {
      userPaused = false;
      play();
    } else {
      userPaused = true;
      video.pause();
    }
  });

  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    if (!visible) {
      if (started) video.pause();
    } else if (!started) {
      maybeStart();
    } else if (!userPaused && !document.hidden) {
      play();
    }
  }, { threshold: 0.1 }).observe(hero);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (started) video.pause();
    } else if (!started) {
      maybeStart();
    } else if (!userPaused && visible) {
      play();
    }
  });

  reduce.addEventListener?.('change', () => {
    if (!reduce.matches) return;
    userPaused = true;
    if (started) video.pause();
  });

  const idle = () => {
    ready = true;
    maybeStart();
  };
  const whenIdle = () => (window.requestIdleCallback
    ? window.requestIdleCallback(idle, { timeout: 2000 })
    : setTimeout(idle, 200));
  if (document.readyState === 'complete') whenIdle();
  else window.addEventListener('load', whenIdle, { once: true });
});

/* ----------------------------------------------------------------------------
 * Live search.
 *
 * Shopify's predictive endpoint renders sections/predictive-search and hands
 * back the markup, so the results are Liquid — the same ticker symbols,
 * backdrops and money formatting as everywhere else — rather than a second
 * product presentation written in JavaScript and drifting from the first.
 *
 * The field is a real <form> pointing at /search, so pressing Enter or having
 * JavaScript fail still runs the search. This only ever adds a preview.
 *
 * Its own scope: search that stops updating is a nuisance, and it must not be
 * able to take the header with it.
 * -------------------------------------------------------------------------- */
part('live search', () => {
  const MIN = 2;          /* one letter matches most of a small catalogue */
  const WAIT = 220;       /* keystrokes per request, not requests per keystroke */
  const LIMIT = 6;

  document.querySelectorAll('[data-predictive]').forEach((form) => {
    const input = form.querySelector('input[name="q"]');
    const panel = form.querySelector('[data-ps]');
    const url = form.dataset.suggestUrl;
    if (!input || !panel || !url) return;

    let timer;
    let pending;
    let hits = [];
    let cursor = -1;

    const close = () => {
      panel.hidden = true;
      panel.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      hits = [];
      cursor = -1;
    };

    const focusHit = (next) => {
      if (!hits.length) return;
      cursor = (next + hits.length) % hits.length;
      hits.forEach((hit, n) => hit.classList.toggle('is-active', n === cursor));
      hits[cursor].scrollIntoView({ block: 'nearest' });
      input.setAttribute('aria-activedescendant', hits[cursor].id);
    };

    const run = async (term) => {
      pending?.abort();
      pending = new AbortController();
      const signal = pending.signal;
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,page',
        'resources[limit]': String(LIMIT),
        section_id: 'predictive-search',
      });
      try {
        const res = await fetch(`${url}?${params}`, { signal });
        if (!res.ok) throw new Error(String(res.status));
        const fresh = new DOMParser()
          .parseFromString(await res.text(), 'text/html')
          .querySelector('[data-ps-results]');
        if (signal.aborted || !fresh) return;
        panel.innerHTML = fresh.innerHTML;
        hits = [...panel.querySelectorAll('[data-ps-hit]')];
        hits.forEach((hit, n) => {
          hit.id = `${panel.id}-hit-${n}`;
          hit.setAttribute('role', 'option');
        });
        cursor = -1;
        panel.hidden = !hits.length && !panel.textContent.trim();
        input.setAttribute('aria-expanded', String(!panel.hidden));
      } catch (err) {
        /* A dropped suggestion is not worth telling anyone about — the field
           still submits to the full results page. */
        if (err.name !== 'AbortError') close();
      }
    };

    input.addEventListener('input', () => {
      const term = input.value.trim();
      clearTimeout(timer);
      if (term.length < MIN) { pending?.abort(); close(); return; }
      timer = setTimeout(() => run(term), WAIT);
    });

    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN && panel.hidden) run(input.value.trim());
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (panel.hidden || !hits.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); focusHit(cursor + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); focusHit(cursor - 1); }
      else if (e.key === 'Enter' && cursor > -1) { e.preventDefault(); hits[cursor].click(); }
    });

    /* A click inside the panel is a result being chosen; the blur that follows
       must not close it out from under the pointer. */
    document.addEventListener('pointerdown', (e) => {
      if (!form.contains(e.target)) close();
    });
    form.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!form.contains(document.activeElement)) close();
      });
    });
  });
});
